import schoolBackendDefaults from './school-backend.defaults.json';
import { apiBaseUrl } from './deployment';
import { SCHOOL_API_PATHS as schoolPaths } from './api-paths';

const backendHost =
  process.env.REACT_APP_BACKEND_HOST || schoolBackendDefaults.host;
const backendPort =
  process.env.REACT_APP_BACKEND_PORT || String(schoolBackendDefaults.port);
const fallbackProductionOrigin = `http://${backendHost}:${backendPort}`;

/**
 * API origin:
 * - REACT_APP_API_URL when set
 * - else derived as https://api.{appBaseDomain} (see deployment.ts)
 * - else '' in development (CRA proxy)
 * - else local backend host:port fallback
 */
const resolvedApiBaseUrl =
  apiBaseUrl() ||
  (process.env.NODE_ENV === 'development' ? '' : fallbackProductionOrigin);

// API Configuration
export const API_CONFIG = {
  // Backend API URL — prefer env/derived deployment hosts (never hardcode product domains here).
  // In development we prefer relative URLs so CRA proxy can avoid CORS preflights.
  BASE_URL: resolvedApiBaseUrl,

  /** Optional dev-only bearer token from env (never treated as a logged-in session). */
  AUTH_TOKEN: process.env.REACT_APP_API_TOKEN || '',

  /** Optional dev-only tenant id from env. */
  TENANT_ID: process.env.REACT_APP_TENANT_ID || '',

  // Request timeout in milliseconds
  TIMEOUT: 10000,

  // Enable debug mode for development
  DEBUG: process.env.NODE_ENV === 'development',

  // Default headers
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
  },

  // Retry configuration
  RETRY: {
    attempts: 3,
    delay: 1000,
  },
};

export const LS_API_TOKEN = 'rust-school-api-token';
export const LS_TENANT_ID = 'rust-school-tenant-id';
export const LS_AUTH_USER = 'rust-school-auth-user';
/** Tenant resolved from `{slug}.app_domain` — survives login credential clears. */
export const LS_HOST_TENANT_ID = 'rust-school-host-tenant-id';

const AUTH_STORAGE_KEYS = [LS_API_TOKEN, LS_TENANT_ID, LS_AUTH_USER] as const;

/** Fired when token or tenant id is saved (currency + module refetch). */
export const TENANT_CREDENTIALS_EVENT = 'rust-school-tenant-changed';

/** Fired when tenant sidebar / navigation catalog should reload (e.g. after reorder). */
export const NAVIGATION_REFRESH_EVENT = 'rust-school-navigation-refresh';

/** Fired when in-app notifications may have changed (e.g. after assignment create). */
export const NOTIFICATIONS_REFRESH_EVENT = 'rust-school-notifications-refresh';

/** Fired when an API call returns 401 with an active bearer token. */
export const AUTH_SESSION_EXPIRED_EVENT = 'rust-school-auth-session-expired';

/**
 * While > 0, ApiClient must not dispatch AUTH_SESSION_EXPIRED_EVENT.
 * Used around login / session restore so a transient 401 (or race while
 * credentials are being swapped) cannot wipe a session mid-establish.
 */
let sessionExpirySuppressDepth = 0;

export function beginSuppressSessionExpiry(): void {
  sessionExpirySuppressDepth += 1;
}

export function endSuppressSessionExpiry(): void {
  sessionExpirySuppressDepth = Math.max(0, sessionExpirySuppressDepth - 1);
}

export function isSessionExpirySuppressed(): boolean {
  return sessionExpirySuppressDepth > 0;
}

export async function withSuppressedSessionExpiry<T>(fn: () => Promise<T>): Promise<T> {
  beginSuppressSessionExpiry();
  try {
    return await fn();
  } finally {
    endSuppressSessionExpiry();
  }
}

function sanitizeAuthToken(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return trimmed.replace(/^bearer\s+/i, '').trim();
}

/**
 * School / org HTTP paths — see `src/config/api-paths.ts` (aligned with backend OpenAPI).
 */
export const SCHOOL_API_PATHS = {
  schoolProfile: schoolPaths.profile,
  schoolBranches: schoolPaths.branches,
  academicYears: schoolPaths.academicYears,
};

function readStorageItem(key: string): string {
  if (typeof localStorage !== 'undefined') {
    const fromLocal = localStorage.getItem(key);
    if (fromLocal) return fromLocal;
  }
  // Older builds stored auth in sessionStorage (tab-scoped). Migrate once.
  if (typeof sessionStorage === 'undefined') return '';
  const fromSession = sessionStorage.getItem(key) || '';
  if (fromSession && typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(key, fromSession);
      sessionStorage.removeItem(key);
    } catch {
      /* storage unavailable — still return the session value */
    }
  }
  return fromSession;
}

function writeStorageItem(key: string, value: string): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(key, value);
  }
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

function removeStorageItem(key: string): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(key);
  }
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

const MIGRATE_AUTH_KEYS = [...AUTH_STORAGE_KEYS, LS_HOST_TENANT_ID] as const;

/** Move tab-scoped session auth into localStorage so login survives new tabs. */
export function purgeLegacyAuthStorage(): void {
  MIGRATE_AUTH_KEYS.forEach((key) => {
    readStorageItem(key);
  });
}

/** Bearer token from the current browser login (shared across tabs). */
export function getSessionAuthToken(): string {
  return readStorageItem(LS_API_TOKEN).trim();
}

/** Tenant id from the current browser login (shared across tabs). */
export function getSessionTenantId(): string {
  return readStorageItem(LS_TENANT_ID).trim();
}

export function getResolvedApiToken(): string {
  const sessionToken = sanitizeAuthToken(getSessionAuthToken());
  if (sessionToken) return sessionToken;
  if (process.env.NODE_ENV === 'development') {
    return sanitizeAuthToken(API_CONFIG.AUTH_TOKEN || '');
  }
  return '';
}

export function getHostTenantId(): string {
  return readStorageItem(LS_HOST_TENANT_ID).trim();
}

export function persistHostTenantId(tenantId: string): void {
  const trimmed = tenantId.trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) return;
  writeStorageItem(LS_HOST_TENANT_ID, trimmed);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(TENANT_CREDENTIALS_EVENT));
  }
}

export function clearHostTenantId(): void {
  if (!getHostTenantId()) return;
  removeStorageItem(LS_HOST_TENANT_ID);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(TENANT_CREDENTIALS_EVENT));
  }
}

export function getResolvedTenantId(): string {
  const sessionTenant = getSessionTenantId();
  if (sessionTenant) return sessionTenant;
  const hostTenant = getHostTenantId();
  if (hostTenant) return hostTenant;
  if (process.env.NODE_ENV === 'development') {
    return API_CONFIG.TENANT_ID || '';
  }
  return '';
}

/** Returns a user-facing message when token/tenant are missing or invalid; null when OK. */
export function validateApiCredentials(): string | null {
  const tenant = getResolvedTenantId().trim();
  const token = getResolvedApiToken().trim();
  if (!tenant) {
    return 'Missing tenant id. Sign in on the login page.';
  }
  if (!/^\d+$/.test(tenant)) {
    return 'Invalid tenant id: the backend expects a numeric tenants.id (e.g. 1), not a UUID. Sign out and sign in again.';
  }
  if (!token) {
    return 'Missing bearer token. Sign in on the login page.';
  }
  return null;
}

export function persistApiCredentials(token?: string, tenantId?: string): void {
  let changed = false;
  if (token != null && token.trim()) {
    writeStorageItem(LS_API_TOKEN, sanitizeAuthToken(token));
    changed = true;
  }
  if (tenantId != null && tenantId.trim() && /^\d+$/.test(tenantId.trim())) {
    writeStorageItem(LS_TENANT_ID, tenantId.trim());
    changed = true;
  }
  if (changed && typeof window !== 'undefined') {
    window.dispatchEvent(new Event(TENANT_CREDENTIALS_EVENT));
  }
}

export function persistAuthUser(raw: string): void {
  writeStorageItem(LS_AUTH_USER, raw);
}

export function readAuthUserRaw(): string | null {
  const raw = readStorageItem(LS_AUTH_USER);
  return raw || null;
}

export function clearApiCredentials(): void {
  AUTH_STORAGE_KEYS.forEach((key) => removeStorageItem(key));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(TENANT_CREDENTIALS_EVENT));
  }
}

export function clearTenantId(): void {
  if (!getSessionTenantId()) return;
  removeStorageItem(LS_TENANT_ID);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(TENANT_CREDENTIALS_EVENT));
  }
}

export function getStoredAuthRole(): string | null {
  const raw = readAuthUserRaw();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { role?: string };
    return typeof parsed.role === 'string' ? parsed.role : null;
  } catch {
    return null;
  }
}

// Export for easy import
export default API_CONFIG;
